import { useDemoState } from "@/state/demoState";
import { useFurnitureManagementState } from "@/state/furnitureManagementState";
import {
    MAX_ROOM_HEIGHT,
    MAX_ROOM_SIZE,
    MIN_ROOM_HEIGHT,
    MIN_ROOM_SIZE,
} from "@/state/RoomConfig/RoomConfig";
import { WallConfig } from "@/state/RoomConfig/WallConfig";
import _ from "lodash";
import { useMemo } from "react";
import { AABB, getModelInstanceAabb } from "@/3d/movement2d/aabb2d";
import { FurnitureInstance } from "@/state/Furniture";
import { round } from "@/3d/math";

export const useRoomSizeRestrictions = () => {
    const roomConfig = useDemoState((state) => state.roomConfig);
    const furnitureInstances = useFurnitureManagementState((state) => state.furnitureInstances);

    const { max, min } = useMemo(() => {
        const leftWallPropsMaxOffset = getWallPropMaxOffset(roomConfig.leftWall);
        const rightWallPropsMaxOffset = getWallPropMaxOffset(roomConfig.rightWall);
        const frontWallPropsMaxOffset = getWallPropMaxOffset(roomConfig.frontWall);
        const backWallPropsMaxOffset = getWallPropMaxOffset(roomConfig.backWall);

        const furnitureSpace = getFurnitureSpace(furnitureInstances);
        const minByFurniture = furnitureSpace
            ? getRoomRestrictionsByFurnitureAab(furnitureSpace)
            : {
                  width: 0,
                  depth: 0,
              };

        return {
            max: {
                width: MAX_ROOM_SIZE,
                depth: MAX_ROOM_SIZE,
                height: MAX_ROOM_HEIGHT,
            },
            min: {
                width: round(
                    Math.max(
                        MIN_ROOM_SIZE,
                        frontWallPropsMaxOffset,
                        backWallPropsMaxOffset,
                        minByFurniture.width
                    )
                ),
                depth: round(
                    Math.max(
                        MIN_ROOM_SIZE,
                        leftWallPropsMaxOffset,
                        rightWallPropsMaxOffset,
                        minByFurniture.depth
                    )
                ),
                height: MIN_ROOM_HEIGHT,
            },
        };
    }, [roomConfig, furnitureInstances]);

    return { max, min };
};

export type RoomSizeRestrictions = ReturnType<typeof useRoomSizeRestrictions>;

const getWallPropMaxOffset = (wall: WallConfig) =>
    _.max(wall.props.map((prop) => prop.offset + prop.width)) ?? 0;

const getRoomRestrictionsByFurnitureAab = (furnitureAab: AABB) => {
    return {
        width: Math.max(Math.abs(furnitureAab.minX), Math.abs(furnitureAab.maxX)) * 2,
        depth: Math.max(Math.abs(furnitureAab.minY), Math.abs(furnitureAab.maxY)) * 2,
    };
};

const getFurnitureSpace = (
    furnitureInstances: Record<string, FurnitureInstance>
): AABB | undefined => {
    const instances = Object.values(furnitureInstances);
    const aabbs = instances.map((instance) => getModelInstanceAabb(instance.instance));

    if (aabbs.length === 0) {
        return undefined;
    }

    let minX = aabbs[0].minX;
    let minY = aabbs[0].minY;
    let maxX = aabbs[0].maxX;
    let maxY = aabbs[0].maxY;

    for (let i = 1; i < aabbs.length; i++) {
        const aabb = aabbs[i];
        if (aabb.minX < minX) {
            minX = aabb.minX;
        }
        if (aabb.minY < minY) {
            minY = aabb.minY;
        }
        if (aabb.maxX > maxX) {
            maxX = aabb.maxX;
        }
        if (aabb.maxY > maxY) {
            maxY = aabb.maxY;
        }
    }

    return new AABB(minX, maxX, minY, maxY);
};
